(() => {
  if (window.wokieeListingWindow) return;

  const containerSelector = '#ProductGridContainer .collection__container';
  const pageSelector = 'wokiee-virtual-page[data-wokiee-page]';
  const cardSelector = '.product-card';
  const placeholderSelector = 'wokiee-virtual-card[data-wokiee-card-id]';
  const managers = new WeakMap();
  let activeManager = null;
  let gridObserver = null;

  const getViewportSettings = () => {
    const mobile = window.innerWidth <= 576;
    const tablet = window.innerWidth <= 1024;
    return {
      loadMargin: mobile ? 3200 : (tablet ? 3600 : 4000),
      unloadMargin: mobile ? 6200 : (tablet ? 7000 : 8000),
      maxActiveCards: mobile ? 64 : (tablet ? 80 : 96),
    };
  };

  const isPageInteractionLocked = () => (
    document.body.classList.contains('overflow-hidden') ||
    !!document.querySelector('modal-dialog.active, menumodal-dialog.active, details.menu-opening')
  );

  const getDistanceFromViewport = (element) => {
    if (!element?.isConnected) return Number.POSITIVE_INFINITY;
    const rect = element.getBoundingClientRect();
    if (rect.bottom < 0) return Math.abs(rect.bottom);
    if (rect.top > window.innerHeight) return rect.top - window.innerHeight;
    return 0;
  };

  class ListingWindowManager {
    constructor(container) {
      this.container = container;
      this.records = new Map();
      this.elementRecords = new WeakMap();
      this.restoreHeights = new Map();
      this.restoreSignature = '';
      this.materializeQueue = new Set();
      this.materializeRaf = null;
      this.pruneTimer = null;
      this.resizeTimer = null;
      this.destroyed = false;
      this.settings = getViewportSettings();
      this.lastScrollAt = performance.now();
      this.lastContainerWidth = container.getBoundingClientRect().width;
      this.handleVisibility = this.handleVisibility.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleModalClosed = this.handleModalClosed.bind(this);
      this.observer = typeof IntersectionObserver === 'function'
        ? new IntersectionObserver(this.handleVisibility, {
          root: null,
          rootMargin: `${this.settings.loadMargin}px 0px`,
          threshold: 0,
        })
        : null;

      this.registerMarkup();
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
      document.body.addEventListener('modalClosed', this.handleModalClosed);
      this.schedulePrune(900);
    }

    getPageNumber(element, fallbackPage = 1) {
      const pageElement = element?.closest?.(pageSelector);
      const value = Number.parseInt(
        element?.dataset?.searchResultPage || pageElement?.dataset?.wokieePage,
        10
      );
      return Number.isFinite(value) ? value : fallbackPage;
    }

    registerMarkup(root = this.container) {
      if (this.destroyed || !root) return;
      const cards = [];
      if (root.matches?.(cardSelector)) cards.push(root);
      root.querySelectorAll?.(cardSelector).forEach((card) => cards.push(card));
      cards.forEach((card) => this.registerCard(card));

      const placeholders = [];
      if (root.matches?.(placeholderSelector)) placeholders.push(root);
      root.querySelectorAll?.(placeholderSelector).forEach((item) => placeholders.push(item));
      placeholders.forEach((placeholder) => {
        const cardId = placeholder.dataset.wokieeCardId;
        const record = this.records.get(cardId);
        if (!record) return;
        record.element = null;
        record.placeholder = placeholder;
        record.active = false;
        this.observeElement(placeholder, record);
      });
    }

    registerCard(card) {
      if (!card?.id) return null;
      let record = this.records.get(card.id);
      if (!record) {
        record = {
          id: card.id,
          page: this.getPageNumber(card),
          element: card,
          placeholder: null,
          html: '',
          height: 0,
          active: true,
          queued: false,
        };
        this.records.set(card.id, record);
      } else {
        record.element = card;
        record.placeholder = null;
        record.active = true;
        record.page = this.getPageNumber(card, record.page);
      }
      this.observeElement(card, record);
      return record;
    }

    observeElement(element, record) {
      if (!element || !record) return;
      this.elementRecords.set(element, record);
      this.observer?.observe(element);
    }

    unobserveElement(element) {
      if (element) this.observer?.unobserve(element);
    }

    handleVisibility(entries) {
      entries.forEach((entry) => {
        const record = this.elementRecords.get(entry.target);
        if (!record) return;
        if (entry.isIntersecting && !record.active) this.queueMaterialize(record);
      });
    }

    queueMaterialize(record) {
      if (!record || record.active || record.queued || !record.placeholder?.isConnected) return;
      record.queued = true;
      this.materializeQueue.add(record);
      if (this.materializeRaf !== null) return;
      this.materializeRaf = window.requestAnimationFrame(() => this.flushMaterializeQueue());
    }

    getColumnCount() {
      const value = Number.parseFloat(
        window.getComputedStyle(this.container).getPropertyValue('--page-grid-value')
      );
      return Number.isFinite(value) && value > 0 ? Math.max(1, Math.round(value)) : 1;
    }

    getOrderedRecords() {
      const ordered = [];
      this.container.querySelectorAll(`${cardSelector}, ${placeholderSelector}`).forEach((element) => {
        const record = this.elementRecords.get(element) || this.records.get(
          element.id || element.dataset?.wokieeCardId
        );
        if (record) ordered.push(record);
      });
      return ordered;
    }

    getRowRecords(record, ordered = this.getOrderedRecords()) {
      const index = ordered.indexOf(record);
      if (index < 0) return record ? [record] : [];
      const columns = this.getColumnCount();
      const rowStart = Math.floor(index / columns) * columns;
      return ordered.slice(rowStart, rowStart + columns);
    }

    flushMaterializeQueue() {
      this.materializeRaf = null;
      if (this.destroyed || !this.materializeQueue.size) return;
      if (isPageInteractionLocked()) {
        window.setTimeout(() => {
          if (this.destroyed || this.materializeRaf !== null || !this.materializeQueue.size) return;
          this.materializeRaf = window.requestAnimationFrame(() => this.flushMaterializeQueue());
        }, 600);
        return;
      }
      const firstRecord = [...this.materializeQueue].sort((first, second) => (
        getDistanceFromViewport(first.placeholder) - getDistanceFromViewport(second.placeholder)
      ))[0];
      // A flex row is one visual unit. Restoring the complete row in a single
      // frame prevents the diagonal/alternating loading pattern on two-column
      // phones and keeps the row height unchanged while markup is reconnected.
      const records = this.getRowRecords(firstRecord)
        .filter((record) => !record.active && record.placeholder?.isConnected);
      const materializedCards = [];

      records.forEach((record) => {
        this.materializeQueue.delete(record);
        record.queued = false;
        const card = this.materializeRecord(record, false);
        if (card) materializedCards.push(card);
      });

      if (materializedCards.length) {
        window.dispatchEvent(new CustomEvent('createProductView', {
          detail: { cards: materializedCards, container: this.container, virtualRestore: true },
        }));
      }

      if (this.materializeQueue.size) {
        this.materializeRaf = window.requestAnimationFrame(() => this.flushMaterializeQueue());
      }
      this.schedulePrune(450);
    }

    materializeRecord(record, dispatchEvent = true) {
      if (!record || record.active) return record?.element || null;
      const placeholder = record.placeholder;
      if (!placeholder?.isConnected || !record.html) return null;

      // Parse in an inert document first. This matches the theme's Infinity
      // import path and guarantees that custom elements see their complete
      // child markup when they are upgraded in the live document.
      const parsed = new DOMParser().parseFromString(record.html, 'text/html');
      const parsedCard = parsed.body.firstElementChild;
      if (!parsedCard) return null;
      const card = document.importNode(parsedCard, true);
      this.stabilizeMaterializedCard(card, record.height);

      this.unobserveElement(placeholder);
      placeholder.replaceWith(card);
      record.element = card;
      record.placeholder = null;
      record.html = '';
      record.active = true;
      this.observeElement(card, record);

      if (dispatchEvent) {
        window.dispatchEvent(new CustomEvent('createProductView', {
          detail: { cards: [card], container: this.container, virtualRestore: true },
        }));
      }
      return card;
    }

    stabilizeMaterializedCard(card, savedHeight) {
      if (!card || !Number.isFinite(savedHeight) || savedHeight <= 0) return;
      card.__wokieeVirtualPreviousMinHeight = card.style.minHeight;
      card.dataset.wokieeVirtualStable = 'true';
      card.style.minHeight = `${Math.round(savedHeight * 1000) / 1000}px`;
    }

    syncFormState(card) {
      card.querySelectorAll('input, textarea, select').forEach((field) => {
        if (field instanceof HTMLInputElement) {
          if (field.type === 'checkbox' || field.type === 'radio') {
            if (field.checked) field.setAttribute('checked', '');
            else field.removeAttribute('checked');
          } else {
            field.setAttribute('value', field.value);
          }
        } else if (field instanceof HTMLTextAreaElement) {
          field.textContent = field.value;
        } else if (field instanceof HTMLSelectElement) {
          [...field.options].forEach((option) => {
            if (option.selected) option.setAttribute('selected', '');
            else option.removeAttribute('selected');
          });
        }
      });
    }

    createPlaceholder(record, height, margins = null) {
      const placeholder = document.createElement('wokiee-virtual-card');
      placeholder.dataset.wokieeCardId = record.id;
      placeholder.dataset.searchResultPage = String(record.page);
      placeholder.setAttribute('aria-hidden', 'true');
      const stableHeight = Math.max(1, Math.round(Number(height) * 1000) / 1000);
      placeholder.style.height = `${stableHeight}px`;
      if (margins) {
        placeholder.style.marginTop = margins.top;
        placeholder.style.marginRight = margins.right;
        placeholder.style.marginBottom = margins.bottom;
        placeholder.style.marginLeft = margins.left;
      }
      return placeholder;
    }

    virtualizeRecords(records) {
      if (!records?.length) return 0;
      const plans = [];

      // Read and serialize the complete row before mutating the live DOM. This
      // avoids repeated layout recalculation between cards in the same row.
      for (const record of records) {
        const card = record?.element;
        if (!record?.active || !card?.isConnected) return 0;
        if (card.contains(document.activeElement) || card.matches?.(':hover')) return 0;
        const rect = card.getBoundingClientRect();
        if (!rect.height) return 0;
        const style = window.getComputedStyle(card);
        this.syncFormState(card);
        const serializedCard = card.cloneNode(true);
        if (card.dataset.wokieeVirtualStable === 'true') {
          serializedCard.style.minHeight = card.__wokieeVirtualPreviousMinHeight || '';
          delete serializedCard.dataset.wokieeVirtualStable;
        }
        plans.push({
          record,
          card,
          height: Math.round(rect.height * 1000) / 1000,
          html: serializedCard.outerHTML,
          placeholder: this.createPlaceholder(record, rect.height, {
            top: style.marginTop,
            right: style.marginRight,
            bottom: style.marginBottom,
            left: style.marginLeft,
          }),
        });
      }

      plans.forEach(({ record, card, height, html, placeholder }) => {
        this.unobserveElement(card);
        card.replaceWith(placeholder);
        record.html = html;
        record.height = height;
        record.element = null;
        record.placeholder = placeholder;
        record.active = false;
        this.observeElement(placeholder, record);
      });
      return plans.length;
    }

    virtualizeRecord(record) {
      return this.virtualizeRecords([record]) === 1;
    }

    getLayoutSignature() {
      return [
        Math.round(window.innerWidth / 20) * 20,
        Math.round(this.container.getBoundingClientRect().width / 20) * 20,
        this.container.className,
      ].join('|');
    }

    estimateCardHeight() {
      const heights = [];
      this.records.forEach((record) => {
        const height = record.active
          ? record.element?.getBoundingClientRect?.().height
          : record.height;
        if (Number.isFinite(height) && height > 0) heights.push(height);
      });
      if (!heights.length) return window.innerWidth <= 576 ? 420 : 360;
      heights.sort((first, second) => first - second);
      return heights[Math.floor(heights.length / 2)];
    }

    getRestoreState() {
      const heights = {};
      this.records.forEach((record) => {
        const measuredHeight = record.active
          ? record.element?.getBoundingClientRect?.().height
          : record.height;
        if (Number.isFinite(measuredHeight) && measuredHeight > 0) {
          heights[record.id] = Math.round(measuredHeight * 1000) / 1000;
        }
      });
      return {
        signature: this.getLayoutSignature(),
        heights,
      };
    }

    setRestoreState(state) {
      this.restoreHeights.clear();
      this.restoreSignature = state?.signature || '';
      if (!state?.heights || this.restoreSignature !== this.getLayoutSignature()) return false;
      Object.entries(state.heights).forEach(([cardId, height]) => {
        const parsedHeight = Number(height);
        if (cardId && Number.isFinite(parsedHeight) && parsedHeight > 0) {
          this.restoreHeights.set(cardId, parsedHeight);
        }
      });
      return this.restoreHeights.size > 0;
    }

    insertSerializedPages(pages, position = 'after') {
      if (this.destroyed || !pages?.length) return false;
      const fragment = document.createDocumentFragment();
      const insertedRecords = [];
      const fallbackHeight = this.estimateCardHeight();

      pages.forEach(({ page, html }) => {
        if (!html) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const parsedPage = parsed.body.querySelector(pageSelector);
        const sourceItems = parsedPage ? [...parsedPage.children] : [...parsed.body.children];
        const pageElement = document.createElement('wokiee-virtual-page');
        pageElement.dataset.wokieePage = String(page);

        sourceItems.forEach((sourceItem) => {
          if (!sourceItem.matches?.(cardSelector) || !sourceItem.id) {
            pageElement.appendChild(document.importNode(sourceItem, true));
            return;
          }
          if (this.records.has(sourceItem.id)) return;
          const record = {
            id: sourceItem.id,
            page: Number.parseInt(sourceItem.dataset.searchResultPage, 10) || page,
            element: null,
            placeholder: null,
            html: sourceItem.outerHTML,
            height: this.restoreHeights.get(sourceItem.id) || fallbackHeight,
            active: false,
            queued: false,
          };
          const placeholder = this.createPlaceholder(record, record.height);
          record.placeholder = placeholder;
          this.records.set(record.id, record);
          this.elementRecords.set(placeholder, record);
          insertedRecords.push(record);
          pageElement.appendChild(placeholder);
        });
        fragment.appendChild(pageElement);
      });

      if (!fragment.childNodes.length) return false;
      if (position === 'before') this.container.insertBefore(fragment, this.container.firstChild);
      else this.container.appendChild(fragment);
      insertedRecords.forEach((record) => this.observer?.observe(record.placeholder));
      return true;
    }

    schedulePrune(delay = 720) {
      if (this.destroyed) return;
      if (this.pruneTimer !== null) window.clearTimeout(this.pruneTimer);
      this.pruneTimer = window.setTimeout(() => {
        this.pruneTimer = null;
        if (isPageInteractionLocked() || performance.now() - this.lastScrollAt < 650) {
          this.schedulePrune(750);
          return;
        }
        const prune = () => this.pruneNow();
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(prune, { timeout: 1200 });
        } else {
          prune();
        }
      }, delay);
    }

    pruneNow() {
      if (this.destroyed || isPageInteractionLocked()) return;
      if (performance.now() - this.lastScrollAt < 650) {
        this.schedulePrune(750);
        return;
      }
      const settings = this.settings;
      const ordered = this.getOrderedRecords();
      const activeRecords = ordered.filter((record) => record.active && record.element?.isConnected);
      if (!activeRecords.length) return;

      const columns = this.getColumnCount();
      const rows = [];
      for (let index = 0; index < ordered.length; index += columns) {
        const row = ordered.slice(index, index + columns);
        if (!row.length || row.some((record) => !record.active || !record.element?.isConnected)) continue;
        const distance = Math.min(...row.map((record) => getDistanceFromViewport(record.element)));
        const beyondUnloadMargin = distance > settings.unloadMargin;
        const beyondHardLimit = activeRecords.length > settings.maxActiveCards && distance > settings.loadMargin;
        if (beyondUnloadMargin || beyondHardLimit) rows.push({ row, distance });
      }

      rows.sort((first, second) => second.distance - first.distance);
      // At most one complete row is removed in each idle task. Real Wokiee
      // cards disconnect several custom elements, so chunking the work is what
      // prevents the small desktop hitch while scrolling down.
      const selected = rows[0];
      if (!selected) return;
      const removed = this.virtualizeRecords(selected.row);
      if (removed && rows.length > 1) this.schedulePrune(25);
    }

    handleScroll() {
      if (isPageInteractionLocked()) return;
      this.lastScrollAt = performance.now();
      this.schedulePrune(800);
    }

    handleResize() {
      if (this.resizeTimer !== null) window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        this.resizeTimer = null;
        const currentWidth = this.container.getBoundingClientRect().width;
        // Mobile keyboards and modal scroll-lock change viewport height and
        // emit `resize`, although the product grid itself did not resize.
        if (Math.abs(currentWidth - this.lastContainerWidth) < 2) return;
        this.lastContainerWidth = currentWidth;
        this.lastScrollAt = performance.now();
        this.container.querySelectorAll('.product-card[data-wokiee-virtual-stable="true"]').forEach((card) => {
          card.style.minHeight = card.__wokieeVirtualPreviousMinHeight || '';
          delete card.dataset.wokieeVirtualStable;
          delete card.__wokieeVirtualPreviousMinHeight;
        });
        const previousSettings = this.settings;
        this.settings = getViewportSettings();
        if (previousSettings.loadMargin !== this.settings.loadMargin) {
          this.observer?.disconnect();
          this.observer = typeof IntersectionObserver === 'function'
            ? new IntersectionObserver(this.handleVisibility, {
              root: null,
              rootMargin: `${this.settings.loadMargin}px 0px`,
              threshold: 0,
            })
            : null;
          this.records.forEach((record) => {
            this.observeElement(record.active ? record.element : record.placeholder, record);
          });
        }
        // A changed grid or orientation can change card heights. Materialize only
        // the nearby placeholders; their exact replacement height is refreshed
        // when they later leave the active window.
        this.records.forEach((record) => {
          if (!record.active && getDistanceFromViewport(record.placeholder) <= this.settings.loadMargin) {
            this.queueMaterialize(record);
          }
        });
        this.schedulePrune(1000);
      }, 220);
    }

    handleModalClosed() {
      this.lastScrollAt = performance.now();
      this.schedulePrune(1400);
    }

    appendPage(items, pageNumber) {
      if (this.destroyed || !items?.length) return [];
      const page = document.createElement('wokiee-virtual-page');
      page.dataset.wokieePage = String(Number.isFinite(pageNumber) ? pageNumber : 1);
      items.forEach((item) => page.appendChild(item));
      this.container.appendChild(page);
      this.registerMarkup(page);
      this.schedulePrune(350);
      return items;
    }

    getLoadedPages() {
      const pages = new Set();
      this.container.querySelectorAll(pageSelector).forEach((page) => {
        const value = Number.parseInt(page.dataset.wokieePage, 10);
        if (Number.isFinite(value)) pages.add(value);
      });
      this.records.forEach((record) => {
        if (Number.isFinite(record.page)) pages.add(record.page);
      });
      return pages;
    }

    getProductIds() {
      return new Set(this.records.keys());
    }

    materializeCard(cardId) {
      const record = this.records.get(cardId);
      if (!record) return null;
      this.materializeQueue.delete(record);
      record.queued = false;
      return this.materializeRecord(record, true);
    }

    getStats() {
      let activeCards = 0;
      let virtualCards = 0;
      this.records.forEach((record) => {
        if (record.active) activeCards += 1;
        else virtualCards += 1;
      });
      return {
        totalCards: this.records.size,
        activeCards,
        virtualCards,
        loadedPages: this.getLoadedPages().size,
      };
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.observer?.disconnect();
      if (this.materializeRaf !== null) window.cancelAnimationFrame(this.materializeRaf);
      if (this.pruneTimer !== null) window.clearTimeout(this.pruneTimer);
      if (this.resizeTimer !== null) window.clearTimeout(this.resizeTimer);
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      document.body.removeEventListener('modalClosed', this.handleModalClosed);
    }
  }

  const getManager = (container = document.querySelector(containerSelector)) => {
    if (!container) return null;
    let manager = managers.get(container);
    if (!manager) {
      if (activeManager && activeManager.container !== container && !activeManager.container.isConnected) {
        activeManager.destroy();
      }
      manager = new ListingWindowManager(container);
      managers.set(container, manager);
    }
    activeManager = manager;
    return manager;
  };

  const init = () => {
    const manager = getManager();
    const productGrid = document.getElementById('ProductGridContainer');
    if (!gridObserver && productGrid && typeof MutationObserver === 'function') {
      gridObserver = new MutationObserver(() => {
        const currentContainer = document.querySelector(containerSelector);
        if (currentContainer && currentContainer !== activeManager?.container) getManager(currentContainer);
      });
      gridObserver.observe(productGrid, { childList: true });
    }
    return manager;
  };

  window.wokieeListingWindow = {
    init,
    appendPage(container, items, pageNumber) {
      return getManager(container)?.appendPage(items, pageNumber) || [];
    },
    getLoadedPages(container) {
      return getManager(container)?.getLoadedPages() || new Set();
    },
    getProductIds(container) {
      return getManager(container)?.getProductIds() || new Set();
    },
    materializeCard(cardId, container) {
      return getManager(container)?.materializeCard(cardId) || null;
    },
    register(root) {
      const container = root?.closest?.('.collection__container') || document.querySelector(containerSelector);
      const manager = getManager(container);
      manager?.registerMarkup(root || container);
      manager?.schedulePrune(350);
      return manager;
    },
    prune() {
      getManager()?.schedulePrune(0);
    },
    getStats() {
      return getManager()?.getStats() || { totalCards: 0, activeCards: 0, virtualCards: 0, loadedPages: 0 };
    },
    getRestoreState(container) {
      return getManager(container)?.getRestoreState() || null;
    },
    setRestoreState(state, container) {
      return getManager(container)?.setRestoreState(state) || false;
    },
    insertSerializedPages(container, pages, position) {
      return getManager(container)?.insertSerializedPages(pages, position) || false;
    },
  };

  window.addEventListener('createProductView', (event) => {
    const root = event?.detail?.container || document.querySelector(containerSelector);
    const manager = getManager(root);
    const cards = event?.detail?.cards;
    if (cards?.length) cards.forEach((card) => manager?.registerMarkup(card));
    else manager?.registerMarkup(root);
    manager?.schedulePrune(event?.detail?.virtualRestore ? 500 : 300);
  });
  window.addEventListener('updateGrid', () => window.requestAnimationFrame(init));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
